import axios, { AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig, AxiosError, AxiosInstance } from 'axios';
import { store } from '../store';
import { RootState } from '../store';
import { logout, getCurrentUser } from '../store/slices/authSlice';

// Extend Axios request config type to include custom properties
interface CustomAxiosRequestConfig extends AxiosRequestConfig {
  requestId?: string;
  retryAttempt?: number;
  isRetry?: boolean;
  _retry?: boolean;
}

// Get the API URL from environment variables
// Default to empty string to use Vite proxy (which forwards to localhost:8000)
const API_URL = import.meta.env.VITE_API_URL || '';

// Create axios instance with base URL pointing to our backend API
const apiService = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  withCredentials: true, // Important for CORS requests with credentials
  timeout: 10000 // 10 second timeout
});

// Track pending requests with their timestamp to expire old ones
interface PendingRequest {
  timestamp: number;
  controller: AbortController;
}

const pendingRequests = new Map<string, PendingRequest>();

// Clean up old pending requests (older than 5 seconds)
const cleanupOldRequests = () => {
  const now = Date.now();
  for (const [requestId, request] of pendingRequests.entries()) {
    if (now - request.timestamp > 5000) {
      pendingRequests.delete(requestId);
    }
  }
};

// Track failed requests to limit retries
const failedRequestsMap = new Map<string, number>();
const MAX_RETRIES = 2; // Allow 3 attempts total (original + 2 retries)

// Reset failed request count after some time
const resetFailedRequestCount = (url: string) => {
  setTimeout(() => {
    failedRequestsMap.delete(url);
  }, 60000); // Reset after 1 minute
};

// Add a circuit breaker implementation at the top of the file
interface CircuitBreakerState {
  state: 'OPEN' | 'CLOSED' | 'HALF_OPEN';
  failures: number;
  lastFailure: number;
  resetTimeout: number;
}

// Circuit breaker configuration - Less aggressive settings
const circuitBreakers = new Map<string, CircuitBreakerState>();
const CIRCUIT_BREAKER_THRESHOLD = 5; // Number of failures before opening (increased from 3)
const CIRCUIT_BREAKER_RESET_TIMEOUT = 10000; // 10 seconds timeout (reduced from 30s)

// Function to get or create a circuit breaker
const getCircuitBreaker = (key: string): CircuitBreakerState => {
  if (!circuitBreakers.has(key)) {
    circuitBreakers.set(key, {
      state: 'CLOSED',
      failures: 0,
      lastFailure: 0,
      resetTimeout: CIRCUIT_BREAKER_RESET_TIMEOUT
    });
  }
  return circuitBreakers.get(key)!;
};

// Update the circuit breaker
const updateCircuitBreaker = (key: string, success: boolean) => {
  const breaker = getCircuitBreaker(key);
  
  if (success) {
    // Success - reset the circuit breaker if it was in HALF_OPEN state
    if (breaker.state === 'HALF_OPEN') {
      breaker.state = 'CLOSED';
      breaker.failures = 0;
      console.log(`[CIRCUIT] Breaker for "${key}" reset to CLOSED after successful request`);
    }
  } else {
    // Failure - increment failure count and check threshold
    breaker.failures++;
    breaker.lastFailure = Date.now();
    
    if (breaker.state === 'CLOSED' && breaker.failures >= CIRCUIT_BREAKER_THRESHOLD) {
      breaker.state = 'OPEN';
      console.log(`[CIRCUIT] Breaker for "${key}" opened after ${breaker.failures} failures`);
      
      // Schedule reset to half-open after timeout
      setTimeout(() => {
        const currentBreaker = getCircuitBreaker(key);
        if (currentBreaker.state === 'OPEN') {
          currentBreaker.state = 'HALF_OPEN';
          console.log(`[CIRCUIT] Breaker for "${key}" changed to HALF_OPEN after timeout`);
        }
      }, breaker.resetTimeout);
    }
  }
};

// Check if a request should be allowed or rejected by the circuit breaker
const shouldAllowRequest = (key: string): boolean => {
  const breaker = getCircuitBreaker(key);
  
  if (breaker.state === 'OPEN') {
    // If circuit is open, reject all requests
    console.log(`[CIRCUIT] Request to "${key}" rejected due to OPEN circuit`);
    return false;
  } else if (breaker.state === 'HALF_OPEN') {
    // In half-open state, allow one test request
    console.log(`[CIRCUIT] Test request to "${key}" allowed in HALF_OPEN state`);
    return true;
  }
  
  // If circuit is closed, allow all requests
  return true;
};

// Function to manually reset circuit breakers (useful for debugging)
const resetCircuitBreaker = (key: string) => {
  const breaker = getCircuitBreaker(key);
  breaker.state = 'CLOSED';
  breaker.failures = 0;
  breaker.lastFailure = 0;
  console.log(`[CIRCUIT] Manually reset breaker for "${key}" to CLOSED state`);
};

// Function to reset all circuit breakers
const resetAllCircuitBreakers = () => {
  for (const [key, breaker] of circuitBreakers.entries()) {
    breaker.state = 'CLOSED';
    breaker.failures = 0;
    breaker.lastFailure = 0;
  }
  console.log('[CIRCUIT] Reset all circuit breakers to CLOSED state');
};

// Function to clear all pending requests
const clearPendingRequests = () => {
  for (const [requestId, request] of pendingRequests.entries()) {
    request.controller.abort();
  }
  pendingRequests.clear();
  console.log('[API] Cleared all pending requests');
};

// Add request interceptor for auth token
apiService.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Get auth token from localStorage if available
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
      console.log(`[API] Adding auth token to request: ${config.method?.toUpperCase()} ${config.url}`, 
        `Token: ${token.substring(0, 20)}...`);
    } else {
      console.log(`[API] No auth token found for request: ${config.method?.toUpperCase()} ${config.url}`);
    }
    config.withCredentials = true; // Ensure credentials are always sent
    
    // Type assertion to use our custom config
    const customConfig = config as CustomAxiosRequestConfig;
    
    // Clean up old pending requests
    cleanupOldRequests();
    
    // Skip duplicate check for retry requests
    if (customConfig.isRetry) {
      return config;
    }
    
    // Generate a request ID based on the URL and method
    // Add params to the ID to differentiate requests with different parameters
    const params = customConfig.params ? JSON.stringify(customConfig.params) : '';
    const requestId = `${config.method}-${config.url}-${params}`;
    
    // Get circuit breaker key based on URL pattern
    const urlPath = config.url?.split('?')[0] || '/';
    const circuitBreakerKey = `${config.method}-${urlPath}`;
    
    // Check if circuit breaker is open for this type of request
    if (!shouldAllowRequest(circuitBreakerKey)) {
      return Promise.reject(new Error(`Circuit breaker is open for ${circuitBreakerKey}`));
    }
    
    // If this request is already pending and not a retriable request, handle appropriately
    if (pendingRequests.has(requestId) && !customConfig.retryAttempt) {
      // For auth requests, just log at debug level since duplicates are normal
      if (requestId.includes('/auth/me') || requestId.includes('/auth/current-user')) {
        console.debug(`[API] Auth request already pending: ${requestId}`);
      } else {
        // For non-auth requests, log at info level
        console.log(`[API] Duplicate request detected: ${requestId}`);
      }
      // Continue with the request - duplicates are allowed but logged
    }
    
    // Create an abort controller for this request
    const controller = new AbortController();
    customConfig.signal = customConfig.signal || controller.signal;
    
    // Store the request as pending with timestamp
    pendingRequests.set(requestId, { 
      timestamp: Date.now(),
      controller
    });
    
    // Add a property to remove this request from pending when completed
    customConfig.requestId = requestId;
    
    // Check if this request has failed too many times
    const failCount = failedRequestsMap.get(requestId) || 0;
    if (failCount > MAX_RETRIES) {
      console.error(`Request to ${requestId} has failed too many times. Aborting.`);
      // Return a rejected promise to prevent the request
      return Promise.reject(new Error(`Maximum retry attempts reached for ${requestId}`));
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling and retries
apiService.interceptors.response.use(
  (response: AxiosResponse) => {
    // Remove the request from pending requests
    const customConfig = response.config as CustomAxiosRequestConfig;
    if (customConfig.requestId) {
      pendingRequests.delete(customConfig.requestId);
    }
    
    // Reset failed request count on success
    const url = response.config.url || '';
    failedRequestsMap.delete(url);
    
    // Update circuit breaker on successful response
    const urlPath = url.split('?')[0];
    const circuitBreakerKey = `${response.config.method}-${urlPath}`;
    updateCircuitBreaker(circuitBreakerKey, true);
    
    return response;
  },
  (error: AxiosError) => {
    const originalRequest = error.config as CustomAxiosRequestConfig;
    const url = originalRequest?.url || '';
    const urlPath = url.split('?')[0];
    const circuitBreakerKey = `${originalRequest?.method}-${urlPath}`;
    
    // Handle authentication errors
    if (error.response?.status === 401) {
      // Clear invalid token
      localStorage.removeItem('authToken');
      
      // Only redirect to login if not already on login page
      if (!window.location.pathname.includes('/login')) {
        // Store the current URL to redirect back after login
        localStorage.setItem('redirectAfterLogin', window.location.pathname);
        window.location.href = '/login';
      }
    }
    
    // Increment failed request count
    const failCount = (failedRequestsMap.get(url) || 0) + 1;
    failedRequestsMap.set(url, failCount);
    
    // Update circuit breaker on error
    updateCircuitBreaker(circuitBreakerKey, false);
    
    return Promise.reject(error);
  }
);

// Type for upload progress callback
type ProgressCallback = (progress: number) => void;

// API service methods
const apiServiceMethods = {
  // GET request
  async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    try {
      return await apiService.get<T>(url, config);
    } catch (error: any) {
      console.error(`Error in api.get('${url}'):`, error);
      error.functionName = `api.get('${url}')`;
      throw error;
    }
  },

  // POST request
  async post<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    try {
      // Special handling for authentication endpoints
      if (url.includes('/auth/')) {
        try {
          const response = await apiService.post<T>(url, data, config);
          return response;
        } catch (error: any) {
          console.error(`Authentication error in api.post('${url}'):`, error);
          
          // Format the error message for auth endpoints
          let errorMessage = 'Authentication failed';
          
          if (error.response) {
            // The request was made and the server responded with a status code
            // that falls out of the range of 2xx
            if (error.response.data && error.response.data.message) {
              errorMessage = error.response.data.message;
            } else if (error.response.status === 401) {
              errorMessage = 'Invalid email or password';
            } else if (error.response.status === 500) {
              errorMessage = 'Server error. Please try again later.';
            }
          } else if (error.request) {
            // The request was made but no response was received
            errorMessage = 'No response from server. Please check your internet connection.';
          }
          
          // Create a standardized error
          const enhancedError = new Error(errorMessage);
          enhancedError.name = 'AuthError';
          (enhancedError as any).status = error.response?.status || 500;
          (enhancedError as any).functionName = `api.post('${url}')`;
          
          throw enhancedError;
        }
      }
      
      // Normal handling for non-auth endpoints
      return await apiService.post<T>(url, data, config);
    } catch (error: any) {
      console.error(`Error in api.post('${url}'):`, error);
      error.functionName = `api.post('${url}')`;
      throw error;
    }
  },

  // PUT request
  async put<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    try {
      return await apiService.put<T>(url, data, config);
    } catch (error: any) {
      console.error(`Error in api.put('${url}'):`, error);
      error.functionName = `api.put('${url}')`;
      throw error;
    }
  },

  // DELETE request
  async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    try {
      return await apiService.delete<T>(url, config);
    } catch (error: any) {
      console.error(`Error in api.delete('${url}'):`, error);
      error.functionName = `api.delete('${url}')`;
      throw error;
    }
  },

  // File upload with progress tracking
  async uploadFile<T = any>(
    url: string,
    file: File,
    contentTypeId: string,
    onProgress?: ProgressCallback,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    try {
      const formData = new FormData();
      formData.append('video', file);
      
      // Add required fields
      formData.append('title', file.name || 'Untitled Video');
      formData.append('content_type_id', contentTypeId);
      formData.append('videostatus', 'original');
      formData.append('status', 'Uploaded');
      
      console.log('Uploading video with content_type_id:', contentTypeId);
      
      // Get the auth token
      const token = localStorage.getItem('authToken');
      
      const uploadConfig: AxiosRequestConfig = {
        ...config,
        headers: {
          'Content-Type': 'multipart/form-data',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
        withCredentials: true,
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            onProgress(percentCompleted);
          }
        },
      };

      const response = await apiService.post<T>(url, formData, uploadConfig);
      
      // Verify the response
      if (!response.data) {
        throw new Error('No response data received from upload');
      }
      
      return response;
    } catch (error: any) {
      console.error(`Error in uploadFile('${url}', ${file.name}):`, error);
      if (error.response?.status === 401) {
        // Handle authentication error
        store.dispatch(logout());
        throw new Error('Authentication required. Please log in again.');
      }
      error.functionName = `uploadFile('${url}', ${file.name})`;
      throw error;
    }
  },

  // Process video with the AI processing backend
  async processVideo<T = any>(
    videoId: string,
  ): Promise<AxiosResponse<T>> {
    try {
      return await apiService.post<T>(`/videos/${videoId}/process`);
    } catch (error: any) {
      console.error(`Error in processVideo(${videoId}):`, error);
      error.functionName = `processVideo('${videoId}')`;
      throw error;
    }
  },

  // Check video processing status
  async getVideoStatus<T = any>(
    videoId: string
  ): Promise<AxiosResponse<T>> {
    try {
      return await apiService.get<T>(`/videos/${videoId}/status`);
    } catch (error: any) {
      console.error(`Error in getVideoStatus(${videoId}):`, error);
      error.functionName = `getVideoStatus('${videoId}')`;
      throw error;
    }
  },

  // Get signed URL for video playback
  async getSignedUrl<T = any>(
    videoUrl: string
  ): Promise<AxiosResponse<T>> {
    try {
      return await apiService.post<T>(`/storage/signed-url`, { 
        filePath: videoUrl,
        expiresIn: 3600 // 1 hour
      });
    } catch (error: any) {
      console.error(`Error in getSignedUrl(${videoUrl}):`, error);
      error.functionName = `getSignedUrl('${videoUrl}')`;
      throw error;
    }
  },

  // Get content types for video uploads
  async getContentTypes<T = any>(): Promise<AxiosResponse<T>> {
    try {
      return await apiService.get<T>(`/videos/content-types`);
    } catch (error: any) {
      console.error(`Error in getContentTypes():`, error);
      error.functionName = `getContentTypes()`;
      throw error;
    }
  },

  // Debug methods for troubleshooting
  async testAuth<T = any>(): Promise<AxiosResponse<T>> {
    try {
      console.log('[API] Testing authentication...');
      return await apiService.get<T>('/debug/auth');
    } catch (error: any) {
      console.error('Error in testAuth():', error);
      error.functionName = 'testAuth()';
      throw error;
    }
  },

  async testConnectivity<T = any>(): Promise<AxiosResponse<T>> {
    try {
      console.log('[API] Testing API connectivity...');
      return await apiService.get<T>('/debug/connectivity');
    } catch (error: any) {
      console.error('Error in testConnectivity():', error);
      error.functionName = 'testConnectivity()';
      throw error;
    }
  },

  // Initialize the API service with the base URL and headers
  init(baseURL: string, headers: any = {}) {
    apiService.defaults.baseURL = baseURL;
    apiService.defaults.headers.common = {
      'Content-Type': 'application/json',
      ...headers
    };
    
    // Add request interceptor to include authentication token
    apiService.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('authToken');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );
    
    // Add response interceptor to handle common errors
    apiService.interceptors.response.use(
      (response) => response,
      (error) => {
        // Handle token expiration - redirect to login
        if (error.response?.status === 401 && !error.config.url.includes('/auth/')) {
          console.warn('Authentication token expired or invalid');
          localStorage.removeItem('authToken');
          // If we have a router, redirect to login
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
        }
        return Promise.reject(error);
      }
    );
  },
  
  /**
   * Make a HEAD request to check resource existence
   * @param url The URL to check
   * @param config Additional axios config
   * @returns Promise with the response
   */
  head: async (url: string, config: AxiosRequestConfig = {}): Promise<AxiosResponse> => {
    try {
      // Get current state for auth token
      const state = store.getState() as RootState;
      const token = state.auth.token || localStorage.getItem('authToken');
      
      // Setup request headers
      const headers: any = {
        ...(config.headers || {})
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      // Make the HEAD request
      console.log(`Making HEAD request to: ${url}`);
      return await axios.head(url, { 
        ...config, 
        headers 
      });
    } catch (error) {
      console.error(`HEAD request failed for ${url}:`, error);
      return Promise.reject(error);
    }
  },

  /**
   * Cancel video processing
   * @param videoId The ID of the video to cancel processing for
   * @returns The response from the server
   */
  cancelVideoProcessing: async (videoId: string) => {
    return await apiService.post(`/videos/${videoId}/cancel`);
  },

  // ============ PROJECT-BASED API METHODS ============

  // Get all projects for the current user
  async getProjects<T = any>(): Promise<AxiosResponse<T>> {
    try {
      return await apiService.get<T>('/projects');
    } catch (error: any) {
      console.error('Error in getProjects():', error);
      error.functionName = 'getProjects()';
      throw error;
    }
  },

  // Get project by ID with all related data
  async getProjectById<T = any>(projectId: string): Promise<AxiosResponse<T>> {
    try {
      return await apiService.get<T>(`/projects/${projectId}`);
    } catch (error: any) {
      console.error(`Error in getProjectById(${projectId}):`, error);
      error.functionName = `getProjectById('${projectId}')`;
      throw error;
    }
  },

  // Create new project
  async createProject<T = any>(projectData: {
    name: string;
    description?: string;
    content_type_id: string;
    tone_new: string;
    desired_timeframe: string;
    target_market: string;
    format_type: string;
  }): Promise<AxiosResponse<T>> {
    try {
      return await apiService.post<T>('/projects', projectData);
    } catch (error: any) {
      console.error('Error in createProject():', error);
      error.functionName = 'createProject()';
      throw error;
    }
  },

  // Update project
  async updateProject<T = any>(
    projectId: string,
    updateData: {
      name?: string;
      description?: string;
      content_type_id?: string;
      tone_new?: string;
      desired_timeframe?: string;
      target_market?: string;
      format_type?: string;
    }
  ): Promise<AxiosResponse<T>> {
    try {
      return await apiService.put<T>(`/projects/${projectId}`, updateData);
    } catch (error: any) {
      console.error(`Error in updateProject(${projectId}):`, error);
      error.functionName = `updateProject('${projectId}')`;
      throw error;
    }
  },

  // Update project status
  async updateProjectStatus<T = any>(
    projectId: string,
    status: string
  ): Promise<AxiosResponse<T>> {
    try {
      return await apiService.put<T>(`/projects/${projectId}`, { status });
    } catch (error: any) {
      console.error(`Error in updateProjectStatus(${projectId}):`, error);
      error.functionName = `updateProjectStatus('${projectId}')`;
      throw error;
    }
  },

  // Delete project
  async deleteProject<T = any>(projectId: string): Promise<AxiosResponse<T>> {
    try {
      return await apiService.delete<T>(`/projects/${projectId}`);
    } catch (error: any) {
      console.error(`Error in deleteProject(${projectId}):`, error);
      error.functionName = `deleteProject('${projectId}')`;
      throw error;
    }
  },

  // Upload multiple videos to a project
  async uploadVideosToProject<T = any>(
    projectId: string,
    files: File[],
    onProgress?: ProgressCallback
  ): Promise<AxiosResponse<T>> {
    try {
      const formData = new FormData();
      
      // Add each video file to the form data
      files.forEach((file, index) => {
        formData.append('videos', file);
      });

      // Get the auth token
      const token = localStorage.getItem('authToken');

      const uploadConfig: AxiosRequestConfig = {
        headers: {
          'Content-Type': 'multipart/form-data',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
        withCredentials: true,
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            onProgress(percentCompleted);
          }
        },
        timeout: 300000, // 5 minutes timeout for large uploads
      };

      const response = await apiService.post<T>(
        `/projects/${projectId}/videos/upload`,
        formData,
        uploadConfig
      );

      if (!response.data) {
        throw new Error('No response data received from upload');
      }

      return response;
    } catch (error: any) {
      console.error(`Error in uploadVideosToProject(${projectId}):`, error);
      
      if (error.response?.status === 401) {
        throw new Error('Authentication required. Please log in again.');
      }
      
      // Handle file size errors with helpful messaging
      if (error.response?.data?.message && 
          (error.response.data.message.includes('exceed') || 
           error.response.data.message.includes('size'))) {
        throw new Error(error.response.data.message);
      }
      
      // Handle network timeouts for large files
      if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
        throw new Error('Upload timed out. The file may be too large or your internet connection is slow. Please try uploading smaller files or check your connection.');
      }
      
      error.functionName = `uploadVideosToProject('${projectId}')`;
      throw error;
    }
  },

  // Get videos in a project
  async getProjectVideos<T = any>(projectId: string): Promise<AxiosResponse<T>> {
    try {
      return await apiService.get<T>(`/projects/${projectId}/videos`);
    } catch (error: any) {
      console.error(`Error in getProjectVideos(${projectId}):`, error);
      error.functionName = `getProjectVideos('${projectId}')`;
      throw error;
    }
  },

  // Update video sequence order within a project
  async updateVideoOrder<T = any>(
    projectId: string,
    videoId: string,
    sequenceOrder: number
  ): Promise<AxiosResponse<T>> {
    try {
      return await apiService.put<T>(
        `/projects/${projectId}/videos/${videoId}/order`,
        { sequence_order: sequenceOrder }
      );
    } catch (error: any) {
      console.error(`Error in updateVideoOrder(${projectId}, ${videoId}):`, error);
      error.functionName = `updateVideoOrder('${projectId}', '${videoId}')`;
      throw error;
    }
  },

  // Remove video from project
  async removeVideoFromProject<T = any>(
    projectId: string,
    videoId: string
  ): Promise<AxiosResponse<T>> {
    try {
      return await apiService.delete<T>(`/projects/${projectId}/videos/${videoId}`);
    } catch (error: any) {
      console.error(`Error in removeVideoFromProject(${projectId}, ${videoId}):`, error);
      error.functionName = `removeVideoFromProject('${projectId}', '${videoId}')`;
      throw error;
    }
  },

  // Process/compile project videos into one final video
  async processProject<T = any>(projectId: string): Promise<AxiosResponse<T>> {
    try {
      return await apiService.post<T>(`/projects/${projectId}/process`);
    } catch (error: any) {
      console.error(`Error in processProject(${projectId}):`, error);
      error.functionName = `processProject('${projectId}')`;
      throw error;
    }
  },

  // Re-process selected videos to generate new versions with potentially better results
  async reprocessProject<T = any>(
    projectId: string, 
    videoIds: string[]
  ): Promise<AxiosResponse<T>> {
    try {
      return await apiService.post<T>(`/projects/${projectId}/reprocess`, {
        video_ids: videoIds
      });
    } catch (error: any) {
      console.error(`Error in reprocessProject(${projectId}):`, error);
      error.functionName = `reprocessProject('${projectId}')`;
      throw error;
    }
  },

  // Get project processing status
  async getProjectStatus<T = any>(projectId: string): Promise<AxiosResponse<T>> {
    try {
      return await apiService.get<T>(`/projects/${projectId}/status`);
    } catch (error: any) {
      console.error(`Error in getProjectStatus(${projectId}):`, error);
      error.functionName = `getProjectStatus('${projectId}')`;
      throw error;
    }
  },

  // Get project preview
  async getProjectPreview<T = any>(projectId: string): Promise<AxiosResponse<T>> {
    try {
      return await apiService.get<T>(`/projects/${projectId}/preview`);
    } catch (error: any) {
      console.error(`Error in getProjectPreview(${projectId}):`, error);
      error.functionName = `getProjectPreview('${projectId}')`;
      throw error;
    }
  },

  // Publish project to social media platforms
  async publishProject<T = any>(
    projectId: string,
    publishData: {
      platforms: string[];
      customMetadata?: {
        [platform: string]: {
          title?: string;
          description?: string;
          tags?: string[];
        };
      };
    }
  ): Promise<AxiosResponse<T>> {
    try {
      return await apiService.post<T>(`/projects/${projectId}/publish`, publishData);
    } catch (error: any) {
      console.error(`Error in publishProject(${projectId}):`, error);
      error.functionName = `publishProject('${projectId}')`;
      throw error;
    }
  },

  // Get project publications
  async getProjectPublications<T = any>(projectId: string): Promise<AxiosResponse<T>> {
    try {
      return await apiService.get<T>(`/projects/${projectId}/publications`);
    } catch (error: any) {
      console.error(`Error in getProjectPublications(${projectId}):`, error);
      error.functionName = `getProjectPublications('${projectId}')`;
      throw error;
    }
  },

  // Get publication status
  async getPublicationStatus<T = any>(
    projectId: string,
    publicationId: string
  ): Promise<AxiosResponse<T>> {
    try {
      return await apiService.get<T>(
        `/projects/${projectId}/publications/${publicationId}/status`
      );
    } catch (error: any) {
      console.error(`Error in getPublicationStatus(${projectId}, ${publicationId}):`, error);
      error.functionName = `getPublicationStatus('${projectId}', '${publicationId}')`;
      throw error;
    }
  },

  // ============ LEGACY VIDEO METHODS (for backward compatibility) ============
  // Note: processVideo and getVideoStatus methods are defined earlier in the file

  // ============ DEBUGGING UTILITIES ============
  // Reset circuit breaker for a specific endpoint
  resetCircuitBreaker: (key: string) => resetCircuitBreaker(key),
  
  // Reset all circuit breakers
  resetAllCircuitBreakers: () => resetAllCircuitBreakers(),
  
  // Clear all pending requests
  clearPendingRequests: () => clearPendingRequests(),
};

export default apiServiceMethods; 